/**
 * Client ของหน้า "แผนการผลิต" (`GET /v1/mps-versions`, `GET /v1/mps-attainment`,
 * D-127/D-110) — เขียนตาม router จริงที่เซิร์ฟเวอร์ serve
 * (`apps/api/src/cap_erp_api/modules/planning/router.py`) ไม่ใช่จาก
 * `docs/API_CONTRACT.md` เพียงอย่างเดียว
 *
 * **client ใหม่ทั้งตัว ไม่ใช่ผู้ใช้ของ `lib/mrp`** ด้วยเหตุผลเดียวกับที่ไฟล์นั้นไม่ใช้
 * ซ้ำจาก `lib/forecasts`: แต่ละ vertical ของ Work Order คนละใบ เขียนโครง ชื่อฟังก์ชัน
 * และชุดความล้มเหลวของตัวเอง
 *
 * หลักการเดียวกับ client อื่นทุกตัวในโปรเจกต์นี้:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 * - payload ที่ผิดรูปคือ `invalid-response` **ไม่ใช่ "ยังไม่มีแผน"** — รายการว่างกับ
 *   โหลดไม่สำเร็จต้องแยกกันให้ขาด
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว — header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * ทั้งสองเส้นเป็น `GET` ล้วน — ไม่มี `POST`/`PATCH` ในไฟล์นี้เลย (D-127: การสร้าง/
 * แก้ไข MPS เป็นงานของหน้าจออื่นที่ยังไม่เกิด)
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  MpsAttainmentItem,
  MpsAttainmentPage,
  MpsFreezeBody,
  MpsLineDetail,
  MpsVersionCreateBody,
  MpsVersionDetail,
  MpsVersionListPage,
  MpsVersionSummary,
  MpsWorkCenterOption,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const MPS_VERSIONS_PATH = '/v1/mps-versions';
export const MPS_ATTAINMENT_PATH = '/v1/mps-attainment';

/** พารามิเตอร์ของ `GET /v1/mps-versions` ที่ router รับ — และไม่มีตัวที่ห้า */
export interface MpsVersionListQuery {
  readonly plant_code?: string;
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * พารามิเตอร์ของ `GET /v1/mps-attainment` — `mps_id` บังคับ ที่เหลือทางเลือกทั้งหมด
 *
 * `plant_code` **ไม่มีในรายการนี้เลย** (D-110 ข้อ 4) — โรงงานมาจากตัวแผนเสมอ ไม่มี
 * ทางส่งโรงงานเข้าไปเองได้
 *
 * `work_center_code` ทางเลือก (D-128 ข้อ 3/4) — กรอง "Line ผลิต" รหัสที่ไม่รู้จักหรือ
 * อยู่โรงงานอื่นได้หน้าว่าง ไม่ใช่ error
 */
export interface MpsAttainmentQuery {
  readonly mps_id: string;
  readonly date_from?: string;
  readonly date_to?: string;
  readonly product_code?: string;
  readonly work_center_code?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

export function buildMpsVersionListQuery(query: MpsVersionListQuery = {}): string {
  const params = new URLSearchParams();
  const plantCode = query.plant_code?.trim() ?? '';
  if (plantCode !== '') {
    params.append('plant_code', plantCode);
  }
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

export function buildMpsAttainmentQuery(query: MpsAttainmentQuery): string {
  const params = new URLSearchParams();
  params.append('mps_id', query.mps_id);
  const dateFrom = query.date_from?.trim() ?? '';
  if (dateFrom !== '') {
    params.append('date_from', dateFrom);
  }
  const dateTo = query.date_to?.trim() ?? '';
  if (dateTo !== '') {
    params.append('date_to', dateTo);
  }
  const productCode = query.product_code?.trim() ?? '';
  if (productCode !== '') {
    params.append('product_code', productCode);
  }
  const workCenterCode = query.work_center_code?.trim() ?? '';
  if (workCenterCode !== '') {
    params.append('work_center_code', workCenterCode);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  return `?${params.toString()}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /** `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่สองเส้นนี้ตอบได้ แยกเป็นสถานะของตัวเอง
 *
 * `not-found` (`404`) คือคำตอบเดียวกันทั้ง "ไม่มี `mps_id` นี้" และ "แผนนี้อยู่นอก
 * ขอบเขตของคุณ" โดยตั้งใจ (D-110 ข้อ 4ก) — ไม่มี `rule`/`details` ใดแยกสองกรณีนี้ออก
 * จากกันได้ และหน้าจอต้องไม่พยายามเดา
 *
 * `rule-violation` (`422`) ถือ `MPS_ATTAINMENT_RANGE_IS_REVERSED`/
 * `MPS_ATTAINMENT_RANGE_TOO_WIDE` ของ `GET /v1/mps-attainment`
 */
export type MpsFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type MpsVersionListResult =
  | { readonly status: 'ready'; readonly page: MpsVersionListPage }
  | MpsFailure;

export type MpsAttainmentResult =
  | { readonly status: 'ready'; readonly page: MpsAttainmentPage }
  | MpsFailure;

export interface MpsRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /** inject ได้เพื่อการทดสอบ — ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับ client อื่นใช้ */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function nullableString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  return requiredString(value);
}

/** `next_cursor` ของ envelope — `undefined` แปลว่า "ผิดรูป" ไม่ใช่ "ไม่มีหน้าถัดไป" */
function parseCursor(raw: unknown): string | null | undefined {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw !== 'string') {
    return undefined;
  }
  return raw.trim() === '' ? null : raw;
}

/**
 * หนึ่งแผนบนรายการ — **ทุกฟิลด์บังคับตาม `MpsVersionListItemResponse`** ยกเว้นสี่
 * ฟิลด์ที่เป็น `Optional` จริงฝั่งเซิร์ฟเวอร์ (`frozen_until`, `approved_by_employee_id`,
 * `approved_at`, `released_at`, `cancelled_at`, `note`)
 */
export function parseMpsVersionSummary(raw: unknown): MpsVersionSummary | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const mpsId = requiredString(record.mps_id);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const versionLabel = requiredString(record.version_label);
  const status = requiredString(record.status);
  const horizonStart = requiredString(record.horizon_start);
  const horizonEnd = requiredString(record.horizon_end);
  const createdByEmployeeId = requiredString(record.created_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    mpsId === null ||
    plantId === null ||
    plantCode === null ||
    versionLabel === null ||
    status === null ||
    horizonStart === null ||
    horizonEnd === null ||
    createdByEmployeeId === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return {
    mps_id: mpsId,
    plant_id: plantId,
    plant_code: plantCode,
    version_label: versionLabel,
    status,
    horizon_start: horizonStart,
    horizon_end: horizonEnd,
    frozen_until: nullableString(record.frozen_until),
    created_by_employee_id: createdByEmployeeId,
    approved_by_employee_id: nullableString(record.approved_by_employee_id),
    approved_at: nullableString(record.approved_at),
    released_at: nullableString(record.released_at),
    cancelled_at: nullableString(record.cancelled_at),
    note: nullableString(record.note),
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parseMpsVersionListPage(raw: unknown): MpsVersionListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: MpsVersionSummary[] = [];
  for (const entry of record.items) {
    const parsed = parseMpsVersionSummary(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

/** `product_name` ของ D-133 — ฟิลด์หาย (`undefined`, เซิร์ฟเวอร์เก่า) = `''` ไม่ใช่ invalid-response; ผิดชนิดยังทำให้ทั้งแถว invalid เหมือนฟิลด์บังคับอื่น */
function tolerantAttainmentProductName(value: unknown): string | null {
  if (value === undefined) {
    return '';
  }
  return typeof value === 'string' ? value : null;
}

/**
 * หนึ่งแถว (สินค้า, วัน) ของผลเทียบ — ทุกฟิลด์บังคับตาม `MpsAttainmentItemResponse`
 * ยกเว้น `attainment_pct` ซึ่งเป็น `null` จริงเมื่อแผนเป็นศูนย์ (D-110 ข้อ 7) และ
 * `product_name`/`model` ที่ทนเซิร์ฟเวอร์เก่าแบบเดียวกับ `work_centers` (D-133 ข้อ 2)
 */
export function parseMpsAttainmentItem(raw: unknown): MpsAttainmentItem | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const plantCode = requiredString(record.plant_code);
  const productCode = requiredString(record.product_code);
  const bucketDate = requiredString(record.bucket_date);
  const plannedQty = requiredString(record.planned_qty);
  const actualGoodQty = requiredString(record.actual_good_qty);
  const productName = tolerantAttainmentProductName(record.product_name);

  if (
    plantCode === null ||
    productCode === null ||
    bucketDate === null ||
    plannedQty === null ||
    actualGoodQty === null ||
    productName === null
  ) {
    return null;
  }

  return {
    plant_code: plantCode,
    product_code: productCode,
    bucket_date: bucketDate,
    planned_qty: plannedQty,
    actual_good_qty: actualGoodQty,
    attainment_pct: nullableString(record.attainment_pct),
    product_name: productName,
    model: nullableString(record.model),
  };
}

/** หนึ่งตัวเลือกของ `work_centers` — ทั้งสองฟิลด์บังคับ (D-128 ข้อ 3) */
export function parseMpsWorkCenterOption(raw: unknown): MpsWorkCenterOption | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const workCenterCode = requiredString(record.work_center_code);
  const name = requiredString(record.name);
  return workCenterCode === null || name === null ? null : { work_center_code: workCenterCode, name };
}

/** `work_centers` ที่หาย (เซิร์ฟเวอร์รุ่นเก่า) = `[]` ไม่ใช่ `invalid-response` (D-128 ข้อ 3) */
function parseMpsWorkCenters(raw: unknown): readonly MpsWorkCenterOption[] | null {
  if (raw === undefined || raw === null) {
    return [];
  }
  if (!Array.isArray(raw)) {
    return null;
  }
  const items: MpsWorkCenterOption[] = [];
  for (const entry of raw) {
    const parsed = parseMpsWorkCenterOption(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }
  return items;
}

export function parseMpsAttainmentPage(raw: unknown): MpsAttainmentPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: MpsAttainmentItem[] = [];
  for (const entry of record.items) {
    const parsed = parseMpsAttainmentItem(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  if (cursor === undefined) {
    return null;
  }
  const workCenters = parseMpsWorkCenters(record.work_centers);
  return workCenters === null ? null : { items, next_cursor: cursor, work_centers: workCenters };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | MpsFailure;

const GET_INIT: RequestInit = { method: 'GET', headers: { Accept: 'application/json' } };

async function requestPayload(
  path: string,
  options: MpsRequestOptions,
  init: RequestInit = GET_INIT,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      ...init,
      credentials: 'same-origin',
      cache: 'no-store',
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/**
 * `GET /v1/mps-versions` — **กรองตามขอบเขตโรงงานของผู้เรียกเสมอ**
 *
 * ไม่ส่ง `plant_code`/`status` จากหน้าจอนี้เลย — dropdown ต้องแสดง **ทุกแผนที่ผู้ใช้
 * เห็น** ทุกสถานะ ให้ผู้ใช้เลือกเองได้แม้ `DRAFT`/`CANCELLED` (D-127 ข้อ 2) ส่วนการ
 * เลือกอัตโนมัติเป็นตรรกะฝั่งหน้าจอ (`mpsView.ts`)
 */
export async function fetchMpsVersions(
  query: MpsVersionListQuery = {},
  options: MpsRequestOptions = {},
): Promise<MpsVersionListResult> {
  const result = await requestPayload(
    `${MPS_VERSIONS_PATH}${buildMpsVersionListQuery(query)}`,
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseMpsVersionListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `GET /v1/mps-attainment` — แถวของแผนหนึ่งใบเทียบกับของจริง (D-110)
 *
 * `mps_id` ที่ไม่มีอยู่จริงหรืออยู่นอกขอบเขตของผู้เรียกตอบ `404` เหมือนกันทั้งคู่โดย
 * ตั้งใจ (D-110 ข้อ 4ก) — หน้าจอต้องแสดง "ไม่พบแผนนี้" ไม่ใช่เดาว่ากรณีไหน
 */
export async function fetchMpsAttainment(
  query: MpsAttainmentQuery,
  options: MpsRequestOptions = {},
): Promise<MpsAttainmentResult> {
  const result = await requestPayload(
    `${MPS_ATTAINMENT_PATH}${buildMpsAttainmentQuery(query)}`,
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseMpsAttainmentPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี */
export function ruleFromMpsFailure(failure: MpsFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/* --------------------------------------- สร้างแผนใหม่ (WO-P2-M5-010, D-129) --- */

export type MpsVersionCreateResult =
  | { readonly status: 'ready'; readonly version: MpsVersionSummary }
  | MpsFailure;

/**
 * payload ของ `POST /v1/mps-versions` ตาม `MpsVersionCreateRequest` เป๊ะ — ไม่มี
 * `line_no`/`uom_code` **และไม่มี `version_label` เมื่อไม่ได้ระบุ** (D-132 ข้อ 2) —
 * หน้าสร้างแผนแบบ Manual ไม่ส่งคีย์นี้อีกแล้ว ปล่อยให้เซิร์ฟเวอร์ออกเลขให้
 */
export function serializeMpsVersionCreateBody(body: MpsVersionCreateBody): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    ...(body.version_label === undefined ? {} : { version_label: body.version_label }),
    horizon_start: body.horizon_start,
    horizon_end: body.horizon_end,
    ...(body.note === undefined ? {} : { note: body.note }),
    lines: body.lines.map((line) => ({
      product_code: line.product_code,
      bucket_date: line.bucket_date,
      planned_qty: line.planned_qty,
    })),
  };
}

/**
 * `POST /v1/mps-versions` (D-096, D-129 ข้อ 1) — header และ lines ในคำขอเดียว คืน
 * แผนสถานะ `DRAFT` เสมอเมื่อสำเร็จ — parse ด้วย `parseMpsVersionSummary` ตัวเดิม
 * (คำตอบมี `lines` เพิ่มมาด้วยซึ่งฟังก์ชันนั้นไม่อ่านอยู่แล้ว ไม่ใช่ field ที่หน้ารายการต้องใช้)
 */
export async function createMpsVersion(
  body: MpsVersionCreateBody,
  options: MpsRequestOptions = {},
): Promise<MpsVersionCreateResult> {
  const result = await requestPayload(MPS_VERSIONS_PATH, options, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(serializeMpsVersionCreateBody(body)),
  });
  if (result.status !== 'payload') {
    return result;
  }
  const version = parseMpsVersionSummary(result.payload);
  return version === null ? { status: 'invalid-response' } : { status: 'ready', version };
}

/* --------------------------------------- รายละเอียดแผน + ขับสถานะ (WO-P2-M5-011, D-129) --- */

export type MpsVersionDetailResult =
  | { readonly status: 'ready'; readonly version: MpsVersionDetail }
  | MpsFailure;

/** หนึ่งแถวของ `lines` — ทุกฟิลด์บังคับตาม `MpsLineResponse` */
export function parseMpsLineDetail(raw: unknown): MpsLineDetail | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const lineNo = record.line_no;
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const bucketDate = requiredString(record.bucket_date);
  const plannedQty = requiredString(record.planned_qty);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    typeof lineNo !== 'number' ||
    productId === null ||
    productCode === null ||
    bucketDate === null ||
    plannedQty === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return {
    line_no: lineNo,
    product_id: productId,
    product_code: productCode,
    bucket_date: bucketDate,
    planned_qty: plannedQty,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * เหมือน `parseMpsVersionSummary` แต่บังคับ `lines` เพิ่ม — ใช้กับทั้ง `GET /{mps_id}`
 * และคำตอบของทั้งสี่ transition ที่คืนรูปเดียวกัน (D-096)
 */
export function parseMpsVersionDetail(raw: unknown): MpsVersionDetail | null {
  const summary = parseMpsVersionSummary(raw);
  if (summary === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.lines)) {
    return null;
  }

  const lines: MpsLineDetail[] = [];
  for (const entry of record.lines) {
    const parsed = parseMpsLineDetail(entry);
    if (parsed === null) {
      return null;
    }
    lines.push(parsed);
  }

  return { ...summary, lines };
}

/** ไม่มี body เลย — ใช้กับ `approve`/`release`/`cancel` (D-096) */
const NO_BODY_POST_INIT: RequestInit = { method: 'POST', headers: { Accept: 'application/json' } };

async function requestMpsVersionDetail(
  path: string,
  options: MpsRequestOptions,
  init: RequestInit,
): Promise<MpsVersionDetailResult> {
  const result = await requestPayload(path, options, init);
  if (result.status !== 'payload') {
    return result;
  }
  const version = parseMpsVersionDetail(result.payload);
  return version === null ? { status: 'invalid-response' } : { status: 'ready', version };
}

/** `GET /v1/mps-versions/{mps_id}` — แผนเดียวพร้อมทุกบรรทัด (D-096) */
export async function fetchMpsVersion(
  mpsId: string,
  options: MpsRequestOptions = {},
): Promise<MpsVersionDetailResult> {
  return requestMpsVersionDetail(`${MPS_VERSIONS_PATH}/${mpsId}`, options, GET_INIT);
}

/** `POST /{mps_id}/approve` — ไม่มี body (D-096, segregation of duties D-010 ตัดสินที่ backend) */
export async function approveMpsVersion(
  mpsId: string,
  options: MpsRequestOptions = {},
): Promise<MpsVersionDetailResult> {
  return requestMpsVersionDetail(`${MPS_VERSIONS_PATH}/${mpsId}/approve`, options, NO_BODY_POST_INIT);
}

/** `POST /{mps_id}/release` — ไม่มี body (D-096) */
export async function releaseMpsVersion(
  mpsId: string,
  options: MpsRequestOptions = {},
): Promise<MpsVersionDetailResult> {
  return requestMpsVersionDetail(`${MPS_VERSIONS_PATH}/${mpsId}/release`, options, NO_BODY_POST_INIT);
}

/** `POST /{mps_id}/cancel` — ไม่มี body (D-096) */
export async function cancelMpsVersion(
  mpsId: string,
  options: MpsRequestOptions = {},
): Promise<MpsVersionDetailResult> {
  return requestMpsVersionDetail(`${MPS_VERSIONS_PATH}/${mpsId}/cancel`, options, NO_BODY_POST_INIT);
}

/** `POST /{mps_id}/freeze` — body `{ frozen_until }` เท่านั้น บังคับเสมอ (D-096) */
export async function freezeMpsVersion(
  mpsId: string,
  body: MpsFreezeBody,
  options: MpsRequestOptions = {},
): Promise<MpsVersionDetailResult> {
  return requestMpsVersionDetail(`${MPS_VERSIONS_PATH}/${mpsId}/freeze`, options, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ frozen_until: body.frozen_until }),
  });
}
